import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  OnboardingSession,
  OnboardingStepData,
  ExtractionStatus,
  OnboardingTargetingData
} from '../state/onboarding/onboarding.state';

// API Response interfaces
export interface OnboardingInitResponse {
  session_id: string;
  current_step: number;
  expires_at: string;
}

export interface OnboardingResumeResponse {
  session_id: string;
  current_step: number;
  expires_at: string;
  step_data: OnboardingStepData;
  extraction_status?: ExtractionStatus;
  extraction_source?: string;
  product_description?: string;
  company_name?: string;
  benefits?: string[];
  pains?: string[];
}

export interface OnboardingStepResponse {
  session_id: string;
  current_step: number;
  step_data: OnboardingStepData;
  user_created?: boolean;
}

export interface OnboardingExtractProductResponse {
  session_id: string;
  extraction_status: ExtractionStatus;
  message: string;
}

export interface OnboardingProductStatusResponse {
  session_id: string;
  extraction_status: ExtractionStatus;
  extraction_source?: string;
  product_description?: string;
  company_name?: string;
  benefits?: string[];
  pains?: string[];
  errors?: string[];
}

export interface OnboardingCompleteResponse {
  user: any;
  icp: any;
  workspace: any;
  token: string;
  lead_generation_status: string;
}

/**
 * A lead row as returned by /onboarding-leads and by the lead-generated broadcast.
 * Both sources use the same field names so they can be merged directly.
 */
export interface OnboardingLeadRow {
  id: number;
  person_name?: string | null;
  person_first_name?: string | null;
  person_last_name?: string | null;
  person_job_title?: string | null;
  person_linkedin_url?: string | null;
  person_location?: string | null;
  person_photo_url?: string | null;
  person_email?: string | null;
  /** locked | unlocked | not_found — same semantics as pipeline persons. */
  email_status?: 'locked' | 'unlocked' | 'not_found' | string | null;
  company_name?: string | null;
  company_domain?: string | null;
  company_industry?: string | null;
  company_employee_count?: number | null;
  company_location?: string | null;
  // Null until the user completes their ICP — onboarding leads are created unscored.
  icp_score?: number | string | null;
  is_enhanced?: boolean;
  enhanced_report_id?: number | null;
  created_at?: string;
}

export interface OnboardingLeadsResponse {
  leads: OnboardingLeadRow[];
  generation_status: string;
  leads_generated: number;
  extraction_status: string;
}

const STORAGE_KEY = 'onboarding_session';

// ngrx-store-localstorage persists the whole auth slice under this key, and
// HeadersInterceptor reads the bearer token from it. Anything auth-related has to go
// through here rather than a standalone `token` key.
const AUTH_STORAGE_KEY = 'auth';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private apiUrl = `${environment.api.url}/simplified-onboarding`;

  constructor(
    private http: HttpClient
  ) {}

  // ============================================
  // Private helpers
  // ============================================

  private getPublicHttpOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }

  /**
   * Get HTTP options with auth token (if available).
   * Used for init and complete to link the session to the logged-in user.
   */
  private getAuthHttpOptions(): { headers: HttpHeaders } {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = this.getStoredAuthToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return { headers: new HttpHeaders(headers) };
  }

  private getStoredAuthToken(): string | null {
    try {
      const authState = localStorage.getItem(AUTH_STORAGE_KEY);

      return authState ? JSON.parse(authState).token || null : null;
    } catch {
      return null;
    }
  }

  // ============================================
  // API Methods
  // ============================================

  /**
   * Initialize a new simplified onboarding session.
   * Sends auth token if available to link session to logged-in user.
   * POST /api/v1/simplified-onboarding/init
   */
  initSession(): Observable<OnboardingInitResponse> {
    const url = `${this.apiUrl}/init`;

    return this.http.post<any>(url, {}, this.getAuthHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          session_id: data.session_id,
          current_step: data.current_step,
          expires_at: data.expires_at
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Resume an existing onboarding session
   * GET /api/v1/simplified-onboarding/{sessionId}/resume
   */
  resumeSession(sessionId: string): Observable<OnboardingResumeResponse> {
    const url = `${this.apiUrl}/${sessionId}/resume`;

    return this.http.get<any>(url, this.getPublicHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          session_id: data.session_id,
          current_step: data.current_step,
          expires_at: data.expires_at,
          step_data: this.normalizeStepData(data.step_data),
          extraction_status: data.extraction_status,
          extraction_source: data.extraction_source,
          product_description: data.product_description,
          company_name: data.company_name,
          benefits: data.benefits,
          pains: data.pains
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Update step data in the onboarding session
   * POST /api/v1/simplified-onboarding/{sessionId}/update-step
   */
  updateStep(sessionId: string, step: number, data: any): Observable<OnboardingStepResponse> {
    const url = `${this.apiUrl}/${sessionId}/update-step`;
    const payload = { step, data };

    return this.http.post<any>(url, payload, this.getPublicHttpOptions()).pipe(
      map(response => {
        const respData = response.data || response;
        return {
          session_id: respData.session_id,
          current_step: respData.current_step,
          step_data: this.normalizeStepData(respData.step_data),
          user_created: respData.user_created
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Trigger product extraction from website URL
   * POST /api/v1/simplified-onboarding/{sessionId}/extract-product
   */
  extractProduct(
    sessionId: string,
    websiteUrl: string,
    companyName?: string
  ): Observable<OnboardingExtractProductResponse> {
    const url = `${this.apiUrl}/${sessionId}/extract-product`;
    const payload: any = { website_url: websiteUrl };

    if (companyName) {
      payload.company_name = companyName;
    }

    return this.http.post<any>(url, payload, this.getPublicHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          session_id: data.session_id,
          extraction_status: data.extraction_status,
          message: data.message
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Get the status of product extraction
   * GET /api/v1/simplified-onboarding/{sessionId}/product-status
   */
  getProductStatus(sessionId: string): Observable<OnboardingProductStatusResponse> {
    const url = `${this.apiUrl}/${sessionId}/product-status`;

    return this.http.get<any>(url, this.getPublicHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          session_id: data.session_id,
          extraction_status: data.extraction_status,
          extraction_source: data.extraction_source,
          product_description: data.product_description,
          company_name: data.company_name,
          benefits: data.benefits,
          pains: data.pains,
          errors: data.errors
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Complete the onboarding process.
   * Sends auth token to authenticate the user for completion.
   *
   * The Step 2 targeting goes in this request body rather than in a preceding
   * update-step call, so the backend cannot read stale or empty targeting.
   *
   * POST /api/v1/simplified-onboarding/{sessionId}/complete
   */
  completeOnboarding(
    sessionId: string,
    targeting?: OnboardingTargetingData
  ): Observable<OnboardingCompleteResponse> {
    const url = `${this.apiUrl}/${sessionId}/complete`;

    return this.http.post<any>(url, targeting ?? {}, this.getAuthHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          user: data.user,
          icp: data.icp,
          workspace: data.workspace,
          token: data.token,
          lead_generation_status: data.lead_generation_status
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Fetch the leads generated by the user's onboarding run, newest first.
   *
   * Needed alongside the lead-generated broadcast because the streamed leads live only
   * in memory — after a reload the dashboard has to read them back from the API.
   *
   * GET /api/v1/onboarding-leads?limit=10
   */
  getOnboardingLeads(limit: number = 10): Observable<OnboardingLeadsResponse> {
    const url = `${environment.api.url}/onboarding-leads?limit=${limit}`;

    return this.http.get<any>(url, this.getAuthHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          leads: data.leads ?? [],
          generation_status: data.generation_status,
          leads_generated: data.leads_generated ?? 0,
          extraction_status: data.extraction_status
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Enhance a daily lead by generating a full SearchPerson report for it.
   *
   * POST /api/v1/daily-leads/{leadId}/enhance
   */
  enhanceLead(leadId: number): Observable<{ search_id: number }> {
    const url = `${environment.api.url}/daily-leads/${leadId}/enhance`;

    return this.http.post<any>(url, {}, this.getAuthHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return { search_id: data.search_id };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Unlock (reveal) the email for an onboarding / daily lead.
   * Costs the same email_retrieval credits as pipeline person unlock.
   *
   * POST /api/v1/daily-leads/{leadId}/unlock-email
   */
  unlockLeadEmail(leadId: number): Observable<{
    id: number;
    person_email: string;
    email_status: string;
    credits_deducted: number;
  }> {
    const url = `${environment.api.url}/daily-leads/${leadId}/unlock-email`;

    return this.http.post<any>(url, {}, this.getAuthHttpOptions()).pipe(
      map(response => {
        const data = response.data || response;
        return {
          id: data.id,
          person_email: data.person_email,
          email_status: data.email_status,
          credits_deducted: data.credits_deducted,
        };
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Mark a lead as converted so it disappears from the dashboard table.
   *
   * PATCH /api/v1/daily-leads/{leadId}/status
   */
  markLeadConverted(leadId: number): Observable<void> {
    const url = `${environment.api.url}/daily-leads/${leadId}/status`;

    return this.http.patch<any>(url, { status: 'converted' }, this.getAuthHttpOptions()).pipe(
      map(() => void 0),
      catchError(error => throwError(() => error))
    );
  }

  // ============================================
  // Session Storage Methods
  // ============================================

  /**
   * Save session data to sessionStorage
   */
  saveSessionToStorage(session: OnboardingSession): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Failed to save onboarding session to storage:', error);
    }
  }

  /**
   * Get session data from sessionStorage
   */
  getSessionFromStorage(): OnboardingSession | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to get onboarding session from storage:', error);
    }
    return null;
  }

  /**
   * Clear session data from sessionStorage
   */
  clearSessionFromStorage(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear onboarding session from storage:', error);
    }
  }

  /**
   * Check if the stored session is still valid (not expired)
   */
  isSessionValid(): boolean {
    const session = this.getSessionFromStorage();
    if (!session || !session.expiresAt) {
      return false;
    }
    return new Date(session.expiresAt) > new Date();
  }

  /**
   * Update the stored session's current step
   */
  updateStoredSessionStep(step: number): void {
    const session = this.getSessionFromStorage();
    if (session) {
      session.currentStep = step;
      this.saveSessionToStorage(session);
    }
  }

  /**
   * Update the stored session's step data
   */
  updateStoredSessionStepData(stepKey: string, data: any): void {
    const session = this.getSessionFromStorage();
    if (session) {
      session.stepData = {
        ...session.stepData,
        [stepKey]: data
      };
      this.saveSessionToStorage(session);
    }
  }

  // ============================================
  // Helper Methods
  // ============================================

  /**
   * Normalize step data from API response to match our interface
   */
  private normalizeStepData(apiStepData: any): OnboardingStepData {
    if (!apiStepData) {
      return {};
    }

    const normalized: OnboardingStepData = {};

    Object.keys(apiStepData).forEach(key => {
      // If key is a number, convert to stepN format
      if (!isNaN(Number(key))) {
        normalized[`step${key}`] = apiStepData[key];
      } else if (key.startsWith('step')) {
        normalized[key] = apiStepData[key];
      } else {
        normalized[key] = apiStepData[key];
      }
    });

    return normalized;
  }
}
